<template>
    <div class="mt-5">
        <delete-confirmation name="document" @confirm="deleteDocument"></delete-confirmation>
        <dispute-dates-modal></dispute-dates-modal>
        <div :class="[{'border-bottom': collapsed}, 'd-flex justify-content-between align-items-end']">
            <div class="h4">
                <a href="#attorneyDocumentsTable" class="mr-2" role="button" @click="collapsed = !collapsed">
                    <i :class="['fas text-muted', collapsed ? 'fa-caret-right' : 'fa-caret-down']"></i>
                </a>
                Compensation Disputes
                <small class="text-muted"><span class="font-xs font-italic">(LWC Form 1008)</span></small>             
            </div>                
            <div v-if="!collapsed" class="btn-toolbar" role="toolbar">
                <div class="input-group input-group-sm mb-1">
                    <div class="input-group-prepend">
                        <span class="input-group-text fas fa-search fa-sm text-muted"></span>
                    </div>
                    <input type="text" class="form-control" placeholder="Search Descriptions" v-model="searchString">
                </div>
                <div class="btn-group ml-2"> 
                    <button type="button" class="btn btn-primary btn-sm mb-1" data-toggle="tooltip" title="New Form 1008" @click="newDispute()"><i class="fas fa-gavel fa-lg"></i></button>
                    <button type="button" class="btn btn-primary btn-sm mb-1" data-toggle="tooltip" title="Upload Form 1008" @click="upload(null)"><i class="fas fa-file-upload fa-lg"></i></button>
                </div>
            </div>
        </div>
        <div id="attorneyDocumentsTable" :class="['collapse', { 'show': !collapsed }]">
            <div v-if="filteredDocuments.length === 0" class="border-top"><h3 class="text-muted text-center mt-3">No Documents.</h3></div>           
            <table v-else class="table table-sm">
                <thead>
                    <tr>
                        <th></th>
                        <th class="fit pr-3">Docket Number</th>                        
                        <th class="fit">Document Type</th>
                        <th>Description</th>
                        <th class="fit">Owner/Linked By</th>
                        <th class="fit">Uploaded/Created</th>
                        <th class="fit">Milestone</th>
                        <th class="fit">Tags</th>
                        <th class="fit"></th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="doc in filteredDocuments">
                        <tr class="table-stripe" :key="'1008-' + doc.id">
                            <td class="fit">
                                <a v-if="doc.associated_documents.length || doc.hasAssociatedNotes" href="#associatedDocuments" data-toggle="collapse" role="button" @click="doc.collapsed = !doc.collapsed">
                                    <i :class="['fas fa-lg text-muted', doc.collapsed ? 'fa-caret-right' : 'fa-caret-down']"></i>
                                </a>                                                    
                            </td>
                            <td>                                
                                <input v-if="doc.editDocketNumber" type="text" class="form-control form-control-sm" v-model="doc.docket_number" @blur="changeDocketNumber(doc)" @keyup.enter="changeDocketNumber(doc)" v-focus>
                                <span v-else @click="$set(doc, 'editDocketNumber', true)">
                                    <span v-if="doc.docket_number">{{ doc.docket_number }}</span>
                                    <span v-else class="font-italic text-muted">Docket Number</span>
                                    <i class="fas fa-pencil-alt fa-xs btn-link"></i>
                                </span>
                            </td>                                                       
                            <td class="fit pr-3">                                
                                <a :href="'/attorney/claims/' + claimId + '/disputes/' + doc.id" :target="doc.packet_exists || !doc.can_edit ? '_blank' : '_self'">
                                    <i :class="['fa-lg text-muted mr-1', doc.packet_exists ? 'fas fa-book' : (doc.can_edit ? 'far fa-file-alt' : ' fas fa-file-contract')]" data-toggle="tooltip" :title="doc.packet_exists ? 'Generated 1008 Packet' : (doc.can_edit ? 'Editable 1008' : 'Uploaded 1008')"></i>
                                    LWC Form 1008
                                </a> 
                            </td>
                            <td>
                                <input v-if="doc.editDescription" type="text" class="form-control form-control-sm" v-model="doc.description" @blur="changeDescription(doc)" @keyup.enter="changeDescription(doc)" v-focus>
                                <span v-else @click="$set(doc, 'editDescription', true)" :title="doc.description">                             
                                    {{ doc.description | truncate(80) }}
                                    <i class="fas fa-pencil-alt fa-xs btn-link"></i>
                                </span>
                            </td>
                            <td class="fit pr-3">{{ doc.owner }}</td>
                            <td class="fit pr-3">{{ doc.created_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                            <td class="fit pr-3">{{ milestone(doc) }}</td>
                            <td class="fit pr-3">
                                <document-tags-button :document="doc"></document-tags-button>                                
                            </td>
                            <td class="fit">
                                <div class="d-flex flex-nowrap flex-row justify-content-end">

                                    <div class="flex-fill text-center">
                                        <a v-if="doc.packet_exists && doc.can_edit" :href="'/attorney/claims/' + claimId + '/disputes/' + doc.id + '?force=true'" class="btn btn-link btn-sm py-0 px-1" title="Edit Form 1008">
                                            <i class="fas fa-edit fa-lg"></i>
                                        </a>
                                        <div v-else class="py-0 px-1">
                                            <i class="fas fa-edit fa-lg fa-fw icon-hidden"></i>
                                        </div>
                                    </div>

                                    <div class="flex-fill text-center">
                                        <button class="btn btn-link btn-sm py-0 px-1" title="Generate 1008 Packet" @click="generate(doc)" :disabled="!!doc.generating">
                                            <i :class="['fas fa-cog fa-lg', {'fa-spin':doc.generating}]"></i>
                                        </button>
                                    </div>

                                    <div class="flex-fill text-center">
                                        <button class="btn btn-link btn-sm py-0 px-1" @click="showDates(doc)">
                                            <i class="fas fa-calendar fa-lg"></i>
                                        </button>
                                    </div>                                                                   

                                    <div class="flex-fill text-center">
                                        <button class="btn btn-link btn-sm py-0 px-1" title="Upload Associated Document" @click="upload(doc)">
                                            <i class="fas fa-file-upload fa-lg"></i>
                                        </button>
                                    </div>                                       

                                    <div class="flex-fill text-center">                                    
                                        <button class="btn btn-link btn-sm py-0 pl-1 pr-0" title="Delete Document" @click="confirmDelete(doc.id, 'dispute')">
                                            <i class="fas fa-trash-alt fa-lg"></i>
                                        </button>
                                    </div>

                                </div>
                            </td>
                        </tr>

                        <template id="associatedDocuments" class="collapse">                           
                            <tr v-show="!doc.collapsed" v-for="associatedDoc in doc.associated_documents" :key="getAssociatedDocumentKey(associatedDoc)">
                                <td class="fit"></td>
                                <td class="fit"></td>
                                <td class="fit">
                                    <div class="d-flex justify-content-start align-items-center">
                                        <!-- using an empty icon element to get consistent indentation -->
                                        <div><i class="fas fa-lg fa-fw align-middle"></i></div>
                                        <div><i :class="['fas fa-sm fa-fw align-middle', {'fa-history text-muted mb-1': associatedDoc.historical}]"></i></div>                                  
                                        <div>
                                            <span v-if="associatedDoc.is_confirmation">
                                                <i class="fas fa-clipboard-check fa-lg fa-fw text-muted mr-1"></i>
                                                <a :href="associatedDoc.href" target="_blank">{{ associatedDoc.document_type.name }}</a>
                                            </span>
                                            <document-type-icon v-else :document="associatedDoc" :href="getDocumentUrl(associatedDoc)"></document-type-icon>
                                        </div>
                                    </div>
                                </td>
                                <td>
                                    <span v-if="associatedDoc.is_confirmation">Confirmation received at {{ associatedDoc.completed_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</span>
                                    <span v-else>{{ associatedDoc.description | truncate(80) }}</span>
                                </td>
                                <td class="fit">{{ associatedDoc.created_by.last_name }}</td>
                                <td class="fit">{{ associatedDoc.created_at | localizeTime('YYYY-MM-DD HH:mm:ss') }}</td>
                                <td></td>
                                <td></td>
                                <td class="fit">
                                    <div class="d-flex flex-nowrap flex-row justify-content-end">

                                        <div v-if="!doc.packet_exists" class="text-center">
                                            <button v-if="associatedDoc.can_unlink"  class="btn btn-link btn-sm py-0 pl-1 pr-0" title="Unlink from Document" @click="unassociate(associatedDoc)">                                                
                                                <i class="fas fa-unlink fa-md"></i>
                                            </button>
                                            <button v-else  class="btn btn-link btn-sm py-0 pl-1 pr-0" title="Delete Document" @click="confirmDelete(associatedDoc.id, 'dispute_association')">                                                
                                                <i class="fas fa-trash-alt fa-lg"></i>
                                            </button>
                                        </div>

                                    </div>                                   
                                </td>
                            </tr>
                            <tr v-show="!doc.collapsed && doc.hasAssociatedNotes" :key="'note-associations-' + doc.id">
                                <td class="fit"></td>
                                <td class="fit"></td>
                                <td class="fit">
                                    <div class="d-flex justify-content-start align-items-center">
                                        <!-- using an empty icon element to get consistent indentation -->
                                        <div><i class="fas fa-fw fa-lg"></i></div>
                                        <div><i class="fas fa-sm fa-fw align-middle'"></i></div> 
                                        <div>
                                            <note-document-icon></note-document-icon>
                                            <a :href="'/attorney/claims/' + claimId + '/documents/' + doc.id + '/notes'" target="_blank">Claim Notes</a>
                                        </div>
                                    </div>
                                </td>
                                <td>Associated Claim Notes</td>
                                <td class="text-right" colspan="5">
                                    <button v-if="!doc.packet_exists" class="btn btn-link btn-sm p-0" title="Unlink from Document" @click="unassociateNotes(doc.id)">                                                
                                        <i class="fas fa-unlink fa-md"></i>
                                    </button>                                    
                                </td>
                            </tr>                              
                        </template>                  
                    </template>
                </tbody>
            </table>
        </div>        
    </div>     
</template>

<script>
import { DocumentType } from '../../constants.js';
import DocumentTypeIcon from '../documents/DocumentTypeIconComponent.vue';
import NoteDocumentIcon from '../icons/NotesDocumentIconComponent.vue';
import DisputeDatesModal from './AttorneyDisputeDatesComponent.vue';
import DocumentTagsButton from '../buttons/DocumentTagsButtonComponent.vue';

export default {
    components: {
        DocumentTypeIcon,
        NoteDocumentIcon,
        DisputeDatesModal,
        DocumentTagsButton,
    },
    props: {
        clientId: Number,
        claimId: Number
    },
    data() {
        return {
            collapsed: false,
            searchString: '',
            documents: [],
            options: [],

            // import constants into the template
            DocumentType: DocumentType            
        }
    },
    computed: {
        filteredDocuments: function () {
            let self = this;
            return self.documents.filter(function (doc) {
                return (doc.description.toLowerCase().indexOf(self.searchString.toLowerCase()) !== -1);
            });
        }
    },
    methods: {
        refresh: function (reset) {
            axios.get('/ajax/attorney/claims/' + this.claimId + '/documents/disputes')
                .then(response => {

                    // reset the collapsed state based on user preferences
                    if (reset) {
                        this.collapsed = response.data.collapsed;
                    }

                    // retain the previous collapsed states
                    let collapseStates = this.documents.map(d => { return {id: d.id, collapsed: d.collapsed}; });                    

                    // set the collapsed flag for each document 
                    this.documents = response.data.documents.map(e => {
                        if (reset) {
                            this.$set(e, 'collapsed', true);
                        }
                        else {                            
                            let doc = collapseStates.find(b => b.id === e.id);                            
                            this.$set(e, 'collapsed', doc ? doc.collapsed : false);
                        }
                        return e; 
                    });

                    // set the upload associated document options
                    this.options = response.data.options;

                    // enable popovers after the DOM has updated
                    this.$nextTick(() => {
                        $('[data-toggle="tooltip"]').tooltip();                      
                    });                    
                })
                .catch(error => {
                    console.error(error);
                });
        },
        getAssociatedDocumentKey: function (document) {
            return  'auth-associated-' + (document.is_confirmation ? 'confirmation-' : '') + document.id;
        },
        newDispute: function () {
            axios.post('/ajax/attorney/claims/' + this.claimId + '/disputes')
                .then(response => {

                    window.location.href = '/attorney/claims/' + this.claimId + '/disputes/' + response.data.document_id;
                })
                .catch(error => {

                    console.log(error);
                })
        },
        changeDescription: function (document) {

            document.editDescription = false;

            // update the model
            this.$emit('update');

            // send the change to the server
            axios.patch('/ajax/attorney/documents/' + document.id + '/description', {
                'description': document.description,
            });            
        },
        changeDocketNumber: function (document) {
            document.editDocketNumber = false;
            this.$emit('update');

            // send the change to the server
            axios.patch('/ajax/attorney/documents/' + document.id + '/docket', {
                'docket_number': document.docket_number,
            });
        },    
        showFilters: function () {

        },   
        upload: function (document) {

            // default to uploading a 1008
            var options = [ { id: DocumentType.LWC_FORM_1008, name: 'LWC Form 1008' }];
            var selected = DocumentType.LWC_FORM_1008;

            // if we are uploading a document to associate with a 1008 then the 1008 will have been passed in
            if (document) {

                options = this.options;
                selected = DocumentType.LWC_FORM_1008_FINAL;
            }

            eventHub.$emit('show-file-upload', {
                claimId: this.claimId,
                url: '/ajax/attorney/claims/' + this.claimId + '/documents/upload',
                options: options,
                documentType: selected,
                associateToDocumentId: document ? document.id : null
            });            
        },
        confirmDelete: function (id, type) {
            eventHub.$emit('show-delete-document-confirmation', {
                id: id,
                type: type
            });
        },        
        deleteDocument: function (value) {
            axios.delete('/ajax/attorney/claims/' + this.claimId + '/documents/' + value.type + '/' + value.id)
                .then(response => {
                    // nothing to do; server side event will cause refresh
                })
                .catch(error => {
                    let message = error.response.data.message;
                    toastr['error'](message, 'Error', {timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true});
                });
        },
        unassociate: function (document) {

            let url = document.historical ?
                `/ajax/attorney/claims/${this.claimId}/historical/documents/unassociate` :
                `/ajax/attorney/claims/${this.claimId}/documents/unassociate`;

            if (document.is_confirmation) {
                url = `/ajax/attorney/claims/${this.claimId}/documents/${document.document_id}/confirmations/unassociate`;                
            }

            axios.post(url, {
                'association_id': document.id
            })
            .catch(error =>  {
                toastr['error'](error.response.data.message, 'Error', { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
            });
        },
        unassociateNotes: function (id) {
            axios.patch('/ajax/attorney/claims/' + this.claimId + '/documents/disputes/' + id + '/notes/unassociate')
            .catch(error =>  {
                toastr['error'](error.response.data.message, 'Error', { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
            });
        },
        generate: function (doc) {
            doc.generating = true;
            axios.post('/ajax/attorney/claims/' + this.claimId + '/disputes/' + doc.id + '/generate', {})
                .then(response => {
                    toastr['success'](response.data.message, 'Generating 1008 Packet', { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
                })
                .catch(error =>  {
                    doc.generating = false;
                    toastr['error'](error.response.data.message, 'Error', { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
                });            
        },
        showDates: function (doc) {
            eventHub.$emit('show-attorney-dispute-dates', doc);
        },
        milestone: function (doc) {

            let retval = null;

            if (doc.filing_date) {
                retval = 'Filed - ' + doc.filing_date;
            }

            if (doc.trial_setting_conference_date) {
                retval = 'TSC - ' + doc.trial_setting_conference_date;
            }

            if (doc.trial_date) {
                retval = 'Trial - ' + doc.trial_date;
            }
            
            if (doc.dismissal_date) {
                retval = 'Dismissed - ' + doc.dismissal_date;
            }
            else if (doc.settlement_date) {
                retval = 'Settled - ' + doc.settlement_date;
            }        
            else if (doc.closing_date) {
                retval = 'Closed - ' + doc.closing_date;
            }

            return retval;
        },
        getDocumentUrl: function (document) {

            let url = document.document_id ?
                `/claims/${this.claimId}/documents/${document.document_id}` :
                `/attorney/claims/${this.claimId}/documents/${document.id}`;

            if (document.historical) {
                url = `/clients/${this.clientId}/claims/${this.claimId}/historical/documents/${document.historical_document_id}`;
            }

            return url;
        }             
    },
    created() {
        this.refresh(true);

        window.Echo.private('claims.' + this.claimId)
            .listen('DisputeDocumentEvent', (e) => {

                if (e.message) {
                    toastr[e.message.level](e.message.message, e.message.title, { timeOut: 10000, positionClass: "toast-top-full-width", closeButton: true });
                }

                this.refresh(false); 
            });        
    },
    directives: {
        focus: {
            inserted: function (el) {
                el.focus();
            }
        }
    }       
}
</script>

<style scoped>
    .font-xs {
        font-size: 0.7em;
    }
    
    .icon-hidden {
       color: transparent; 
    }
    
    .table-stripe  {
        background-color:rgba(0,0,0,.05);
    }    
</style>